import { useState } from 'react';

export default function SectionFinalCTA() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => data.append(key, value as string));
    try {
      await fetch('https://readdy.ai/api/form/d6vun0vb245v7h34ot10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } catch { /* noop */ }
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <section className="relative py-24 overflow-hidden" style={{ background: 'linear-gradient(160deg, #040810 0%, #081020 55%, #0d1a30 100%)' }}>
      {/* Top gold line */}
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: 'linear-gradient(90deg, #c9a84c, #e8cc85, #c9a84c)' }} />

      {/* Background texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />

      {/* Large radial glow center */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] opacity-[0.06]" style={{ background: 'radial-gradient(ellipse, #c9a84c, transparent 70%)' }} />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-[11px] font-bold uppercase tracking-[0.3em]">Free Consultation</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2
            className="text-white font-bold leading-[1.05] mb-5"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4.5vw, 62px)' }}
          >
            Speak With a Defense Attorney{' '}
            <span
              className="italic"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #e8cc85)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
            >
              Today
            </span>
          </h2>
          <p className="text-white/50 text-xl leading-relaxed max-w-xl mx-auto">
            Every hour without representation is a risk. Don&apos;t face charges alone.
          </p>
        </div>

        {/* Primary CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
          <a href="tel:9795300250"
            className="inline-flex items-center gap-2.5 px-10 py-4 font-bold text-[15px] rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #e8cc85)', color: '#040810' }}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-phone-fill text-base" />
            </div>
            Call (979) 530-0250
          </a>

          <div className="flex items-center gap-3 text-white/25">
            <div className="w-8 h-[1px] bg-white/15" />
            <span className="text-xs uppercase tracking-widest">or complete form</span>
            <div className="w-8 h-[1px] bg-white/15" />
          </div>
        </div>

        {/* Lead form */}
        <div className="max-w-3xl mx-auto">
          {submitted ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-14 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-6" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8cc85)' }}>
                <i className="ri-check-line text-[#040810] text-2xl" />
              </div>
              <h3 className="text-white text-2xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Message Received
              </h3>
              <p className="text-white/50 text-base leading-relaxed max-w-sm mx-auto">
                We&apos;ll respond within 24 hours. For urgent matters,{' '}
                <a href="tel:9795300250" className="text-[#c9a84c] font-semibold cursor-pointer hover:underline">call us directly</a>.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="final-cta-form"
              onSubmit={handleSubmit}
              className="bg-white/4 border border-white/10 rounded-2xl overflow-hidden"
            >
              {/* Form header bar */}
              <div className="bg-white/4 border-b border-white/8 px-7 py-5">
                <h3 className="text-white text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Schedule Your Free Consultation
                </h3>
                <p className="text-white/35 text-sm mt-0.5">Completely confidential. No obligation. Response within 24 hours.</p>
              </div>

              <div className="p-7">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-white/50 text-[11px] font-bold uppercase tracking-wider mb-2" htmlFor="cta-name">
                      Full Name *
                    </label>
                    <input
                      id="cta-name"
                      name="name"
                      type="text"
                      required
                      placeholder="John Smith"
                      className="w-full px-4 py-3.5 bg-white/6 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/60 focus:bg-white/8 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] font-bold uppercase tracking-wider mb-2" htmlFor="cta-phone">
                      Phone Number *
                    </label>
                    <input
                      id="cta-phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="(979) 555-0000"
                      className="w-full px-4 py-3.5 bg-white/6 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/60 focus:bg-white/8 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] font-bold uppercase tracking-wider mb-2" htmlFor="cta-email">
                      Email Address
                    </label>
                    <input
                      id="cta-email"
                      name="email"
                      type="email"
                      placeholder="john@email.com"
                      className="w-full px-4 py-3.5 bg-white/6 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/60 focus:bg-white/8 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-[11px] font-bold uppercase tracking-wider mb-2" htmlFor="cta-case">
                      Case Type
                    </label>
                    <select
                      id="cta-case"
                      name="case_type"
                      className="w-full px-4 py-3.5 bg-white/6 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-[#c9a84c]/60 transition-all"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="" className="bg-[#050a12]">Select case type...</option>
                      <option value="DUI Defense" className="bg-[#050a12]">DUI Defense</option>
                      <option value="Drug Charges" className="bg-[#050a12]">Drug Charges</option>
                      <option value="Domestic Violence" className="bg-[#050a12]">Domestic Violence</option>
                      <option value="Assault / Violent Crimes" className="bg-[#050a12]">Assault / Violent Crimes</option>
                      <option value="Theft / Property Crimes" className="bg-[#050a12]">Theft / Property Crimes</option>
                      <option value="White Collar" className="bg-[#050a12]">White Collar</option>
                      <option value="Family Law" className="bg-[#050a12]">Family Law</option>
                      <option value="Other" className="bg-[#050a12]">Other</option>
                    </select>
                  </div>
                </div>
                <div className="mb-5">
                  <label className="block text-white/50 text-[11px] font-bold uppercase tracking-wider mb-2" htmlFor="cta-msg">
                    Brief Description
                  </label>
                  <textarea
                    id="cta-msg"
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Briefly describe your situation..."
                    className="w-full px-4 py-3.5 bg-white/6 border border-white/10 rounded-xl text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#c9a84c]/60 focus:bg-white/8 transition-all resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 font-bold text-base rounded-xl transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #dbb85a)', color: '#040810' }}
                >
                  {submitting ? 'Sending...' : 'Submit Free Consultation Request'}
                </button>
                <p className="text-white/25 text-xs text-center mt-3">
                  Protected by attorney-client privilege. No obligation. No spam.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
